<script>
import { mapStores } from "pinia";

import { mapGendersByLevel } from "@/common/level";
import ActionWithTooltip from "@/components/ActionWithTooltip.vue";
import HeaderElement from "@/components/article/HeaderElement.vue";
import InflectionSchemeLink from "@/components/article/InflectionSchemeLink.vue";
import AudioButton from "@/components/AudioButton.vue";
import showInflectionSchemeButtonMixin from "@/components/mixins/article/dictsys/show-inflection-scheme-button";
import TouchOrMouseElement from "@/components/TouchOrMouseElement.vue";
import lang from "@/directives/lang";
import { MatomoHelper } from "@/helpers";
import { useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import Inflections from "./Inflections.vue";
import { shouldShowDanish, shouldShowLatin } from "./level-mapping";

export default {
  name: "ArticleContentHeader",
  components: {
    ActionWithTooltip,
    AudioButton,
    HeaderElement,
    Inflections,
    InflectionSchemeLink,
    TouchOrMouseElement
  },
  directives: {
    lang
  },
  mixins: [showInflectionSchemeButtonMixin],
  props: {
    lemmas: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapStores(useSettingsStore),

    wordclassLevel() {
      return this.settingsStore.activeWordClassLevel;
    },

    showDanishWordClass() {
      return shouldShowDanish(this.wordclassLevel);
    },
    showLatinWordClass() {
      return shouldShowLatin(this.wordclassLevel);
    }
  },
  methods: {
    getMetadata(metadata) {
      return [
        metadata.varieties.join(", "),
        metadata.usages.join(", "),
        metadata.categories.join(", ")
      ]
        .filter(Boolean)
        .join(", ");
    },
    mapGendersToCurrentLevel(genders) {
      return mapGendersByLevel(
        genders,
        this.wordclassLevel,
        this.settingsStore.activeLanguageGroup
      ).join("/");
    },
    trackPronunciationClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Results,
        MatomoEventAction.ClickPronunciation,
        "Pronunciation on headword"
      );
    },
    trackInflectionSchemeClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Results,
        MatomoEventAction.ClickInflectionScheme,
        "Inflection scheme on headword"
      );
    }
  }
};
</script>

<template>
  <div class="article-content__header article-header justify-content-between">
    <div class="article-header__content">
      <div v-for="(lemma, lemmaIndex) in lemmas" :key="lemmaIndex" class="d-inline-block">
        <h5 v-lang:source class="article-header__word">
          {{ lemma.headWord }}<template v-if="lemma.metadata.trademark">&reg;</template>
          <!-- Zero-width space to prevent funky highlighting behavior -->
          &#8203;
        </h5>

        <HeaderElement
          v-if="
            lemma.metadata.varieties.length > 0 ||
            lemma.metadata.usages.length > 0 ||
            lemma.metadata.categories.length > 0
          "
          v-lang:da
          :content="getMetadata(lemma.metadata)"
          class="article-header__element--comment article-header__element--chained"
        />

        <Inflections
          v-if="!showInflectionSchemeButton(lemma)"
          :inflections="lemma.inflections.long"
          lang="source"
          class="article-header__inflections"
        />

        <HeaderElement
          v-if="lemma.wordclass && showDanishWordClass"
          v-lang:da
          :content="lemma.wordclass.danish"
          :wrapped="false"
          class="article-header__element--light"
        />
        <HeaderElement
          v-if="lemma.wordclass && showLatinWordClass"
          v-lang:da
          :content="lemma.wordclass.danishLatin"
          :wrapped="showDanishWordClass && showLatinWordClass"
          class="article-header__element--light article-header__element--chained"
        />
        <template v-if="lemma.metadata.genders.length > 0">
          <i class="article-header__element--light">, </i>
          <HeaderElement
            v-lang:da
            :wrapped="false"
            :content="mapGendersToCurrentLevel(lemma.metadata.genders)"
            class="article-header__element--light article-header__element--chained"
          />
        </template>

        <div
          v-if="lemma.metadata.irregular || lemma.grammaticalComment"
          class="article-header__element article-header__element--comment d-inline-block"
        >
          <i v-if="lemma.metadata.irregular" v-lang:da>{{ $t("article.metadata.irregular") }}</i>
          <i v-if="lemma.metadata.irregular && lemma.grammaticalComment">, </i>
          <i v-if="lemma.grammaticalComment" v-lang:da>{{ lemma.grammaticalComment }}</i>
        </div>

        <span v-if="lemmaIndex < lemmas.length - 1" class="article-header__word-separator">, </span>
      </div>
    </div>

    <div class="article-header__controls">
      <template v-for="(lemma, lemmaIndex) in lemmas" :key="lemmaIndex">
        <TouchOrMouseElement class="d-inline-flex">
          <template #mouse>
            <ActionWithTooltip hide-on-click class="article-header__btn">
              <template #element>
                <AudioButton
                  v-if="lemma.pronunciationFileUrl"
                  :audio-url="lemma.pronunciationFileUrl"
                  :aria-label="$t('article.pronunciation.label')"
                  @click="trackPronunciationClick()"
                />
              </template>
              <template #actionInitial>{{ $t("article.pronunciation.label") }}</template>
            </ActionWithTooltip>
            <ActionWithTooltip hide-on-click class="article-header__btn">
              <template #element>
                <InflectionSchemeLink
                  v-if="showInflectionSchemeButton(lemma)"
                  :word="lemma.headWord"
                  :section-id="`${lemmaIndex}`"
                  :article-id="lemma.id"
                  @click="trackInflectionSchemeClick()"
                />
              </template>
              <template #actionInitial>{{ $t("article.actions.conjugation.open") }}</template>
            </ActionWithTooltip>
          </template>
          <template #touch>
            <AudioButton
              v-if="lemma.pronunciationFileUrl"
              class="article-header__btn"
              :audio-url="lemma.pronunciationFileUrl"
              :aria-label="$t('article.pronunciation.label')"
              @click="trackPronunciationClick()"
            />

            <InflectionSchemeLink
              v-if="showInflectionSchemeButton(lemma)"
              class="article-header__btn"
              :word="lemma.headWord"
              :section-id="`${lemmaIndex}`"
              :article-id="lemma.id"
              @click="trackInflectionSchemeClick()"
            />
          </template>
        </TouchOrMouseElement>
      </template>
    </div>
  </div>
</template>
